/** @flow */
import * as React from 'react';
import styled, { css } from 'styled-components';
import { Map, GeoJSON } from 'react-leaflet';
import Leaflet from 'leaflet';
import { roundNumber, calculatePercent } from 'common/helpers/math';
import { displayPercent } from 'common/components/Number';
import { interpolateColor } from 'common/helpers/colorScale';
import GeolocationReach from 'statsradio/modules/dashboard/domain/GeolocationReach';

const FEATURE_JOIN_KEY = 'code';

export type ChoroplethTheme = {
  minColor: string,
  maxColor: string,
  hoverColor: string,
  tooltipBackgroundColor: string,
};

export type Viewport = { center: [number, number], zoom: number };

type Props = {
  id?: string,
  map: Object,
  geolocalisation: GeolocationReach[],
  viewport?: Viewport,
  onViewportChange?: (viewport: Viewport) => void,
  zoomable?: boolean,
  height?: number,
  aspectRatio?: number,
  reachLabel: string,
  theme: ChoroplethTheme,
  showLegend?: boolean,
};

// Strips Leaflet's default boxed tooltip chrome so the permanent region-name
// label reads as plain text sitting on the map, not a callout.
const permanentLabelStyles = css`
  .leaflet-tooltip.choropleth-reach-map-label {
    background: none;
    border: none;
    box-shadow: none;
    padding: 0;
    color: #333;
    font-size: 11px;
    font-weight: 600;
    text-shadow: 0 0 3px #fff, 0 0 3px #fff;
    pointer-events: none;
  }
`;

const FixedHeightWrapper = styled.div`
  height: ${({ height }) => height}px;

  .leaflet-container {
    height: 100%;
    background: none;
  }

  ${permanentLabelStyles}
`;

const AspectRatioWrapper = styled.div`
  position: relative;
  width: 100%;
  padding-top: ${({ ratio }) => ratio}%;

  .leaflet-container {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: none;
  }

  ${permanentLabelStyles}
`;

export const Legend = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 8px;
  font-size: 12px;
  color: #333;
`;

const LegendGradient = styled.div`
  flex: 0 1 300px;
  height: 10px;
  border-radius: 2px;
  background: ${({ gradient }) => gradient};
`;

const LEGEND_GRADIENT_STEPS = 10;

// A region with 5% of a 95%-max spread is barely distinguishable from an
// empty one on a linear scale, which is the opposite of what Highcharts'
// perceptual scale gave us. Interpolating on sqrt(value) instead of value
// front-loads contrast onto the low end, so small-but-nonzero reach still
// reads as visibly colored while 0% stays untouched (sqrt(0) === 0).
function reachToFillColor(theme: ChoroplethTheme, value: number, maxReach: number): string {
  return interpolateColor(theme.minColor, theme.maxColor, 0, Math.sqrt(maxReach), Math.sqrt(value));
}

function buildLegendGradient(theme: ChoroplethTheme, maxReach: number): string {
  const stops = Array.from({ length: LEGEND_GRADIENT_STEPS + 1 }, (_, step) => {
    const value = (maxReach / LEGEND_GRADIENT_STEPS) * step;
    const position = (step / LEGEND_GRADIENT_STEPS) * 100;
    return `${reachToFillColor(theme, value, maxReach)} ${position}%`;
  });
  return `linear-gradient(to right, ${stops.join(', ')})`;
}

export function buildReachByCode(geolocalisation: GeolocationReach[]): { [code: string]: number } {
  const total = geolocalisation.reduce((acc, reach) => acc + reach.reach, 0);

  // Handle all-zero case: return 0 for every code to avoid NaN propagation
  if (total === 0 || geolocalisation.length === 0) {
    return geolocalisation.reduce((acc, reach) => {
      acc[reach.getMapKey()] = 0;
      return acc;
    }, {});
  }

  return geolocalisation.reduce((acc, reach) => {
    acc[reach.getMapKey()] = roundNumber(calculatePercent(reach.reach, total), 2);
    return acc;
  }, {});
}

export function findRegionWithMaxReach(geolocalisation: GeolocationReach[]): ?GeolocationReach {
  if (geolocalisation.length === 0) {
    return undefined;
  }

  return geolocalisation.reduce((prev, current) => (prev.reach > current.reach ? prev : current));
}

class ChoroplethReachMap extends React.Component<Props> {
  mapRef = React.createRef();

  buildDataKey(): string {
    const { geolocalisation, map } = this.props;
    const reachKey = geolocalisation.map(r => `${r.getMapKey()}:${r.reach}`).join('|');
    return `${reachKey}:${map.features.length}`;
  }

  getReachByCode(): { [code: string]: number } {
    return buildReachByCode(this.props.geolocalisation);
  }

  getMaxReach(reachByCode: { [code: string]: number }): number {
    return Math.max(1, ...Object.keys(reachByCode).map(code => reachByCode[code]));
  }

  featureStyle = (feature: Object) => {
    const { theme } = this.props;
    const reachByCode = this.getReachByCode();
    const value = reachByCode[feature.properties[FEATURE_JOIN_KEY]] || 0;

    return {
      fillColor: reachToFillColor(theme, value, this.getMaxReach(reachByCode)),
      fillOpacity: 1,
      color: '#ccc',
      weight: 1,
    };
  };

  onEachFeature = (feature: Object, layer: Object) => {
    const { reachLabel, theme } = this.props;
    const reachByCode = this.getReachByCode();
    const value = reachByCode[feature.properties[FEATURE_JOIN_KEY]] || 0;

    const nameLabel = feature.properties.name;
    const boxStyle = `background:${theme.tooltipBackgroundColor}; padding:6px; border-radius:3px; box-shadow:0 1px 3px rgba(0,0,0,0.4);`;
    const nameAndValueLabel = `<div style="${boxStyle}">${feature.properties.name}<br />`
      + `<strong>${reachLabel} ${displayPercent(value / 100)}</strong></div>`;
    const hasReach = value > 0;

    if (hasReach) {
      layer.bindTooltip(nameLabel, {
        permanent: true,
        direction: 'center',
        className: 'choropleth-reach-map-label',
      });
    } else {
      layer.bindTooltip(nameAndValueLabel, { sticky: true, className: 'choropleth-reach-map-tooltip' });
    }

    layer.on('mouseover', () => {
      layer.setStyle({ fillColor: theme.hoverColor });
      if (hasReach) {
        layer.setTooltipContent(nameAndValueLabel);
      }
    });
    layer.on('mouseout', () => {
      layer.setStyle(this.featureStyle(feature));
      if (hasReach) {
        layer.setTooltipContent(nameLabel);
      }
    });
  };

  onGeoJsonAdd = (event: Object) => {
    const { viewport, geolocalisation } = this.props;
    if (viewport) {
      return;
    }

    const regionWithMaxReach = findRegionWithMaxReach(geolocalisation);
    const mapInstance = this.mapRef.current;
    if (!regionWithMaxReach || !mapInstance) {
      return;
    }

    const regionCode = regionWithMaxReach.getMapKey();
    let matchedBounds = null;

    event.target.eachLayer((featureLayer) => {
      if (featureLayer.feature.properties[FEATURE_JOIN_KEY] === regionCode) {
        matchedBounds = featureLayer.getBounds();
      }
    });

    if (matchedBounds) {
      mapInstance.leafletElement.fitBounds(matchedBounds);
    }
  };

  render() {
    const {
      id, map, viewport, onViewportChange = () => {}, zoomable, height = 300, aspectRatio, showLegend, theme,
    } = this.props;
    const Wrapper = aspectRatio ? AspectRatioWrapper : FixedHeightWrapper;
    const hasFeatures = !!(map && Array.isArray(map.features) && map.features.length > 0);

    if (!hasFeatures) {
      return <Wrapper id={id} height={height} ratio={aspectRatio} />;
    }

    const initialBounds = viewport ? undefined : Leaflet.geoJSON(map).getBounds();
    const maxReach = this.getMaxReach(this.getReachByCode());

    return (
      <Wrapper id={id} height={height} ratio={aspectRatio}>
        <Map
          ref={this.mapRef}
          bounds={initialBounds}
          viewport={viewport}
          onViewportChanged={onViewportChange}
          zoomControl={zoomable}
          scrollWheelZoom={zoomable}
          dragging={zoomable}
          doubleClickZoom={zoomable}
          touchZoom={zoomable}
          attributionControl={false}
        >
          <GeoJSON
            key={this.buildDataKey()}
            data={map}
            style={this.featureStyle}
            onEachFeature={this.onEachFeature}
            onAdd={this.onGeoJsonAdd}
          />
        </Map>
        {showLegend && (
          <Legend>
            <span>0%</span>
            <LegendGradient gradient={buildLegendGradient(theme, maxReach)} />
            <span>{displayPercent(maxReach / 100)}</span>
          </Legend>
        )}
      </Wrapper>
    );
  }
}

export default ChoroplethReachMap;



// WEBPACK FOOTER //
// ./src/statsradio/components/maps/ChoroplethReachMap.js